import debounce from 'lodash/debounce';

import Localize from 'app/src/localize/Localize';
import GiftCardManager from 'app/src/payments/GiftCardManager';

import * as View from './GiftCardView';
import { Customer, GIFT_CARD_STATUS, GiftCard } from './types';

const SEARCH_DEBOUNCE_RATE_MS = 1000;
const MAX_PERSONAL_MESSAGE_LENGTH = 200;

const refreshGiftCardInputs = debounce(async (code: string): Promise<void> => {
  // Ensures the debounce is cancelled when the code is invalid.
  if (code.length < 8) {
    return;
  }

  let giftCards: GiftCard[];

  try {
    giftCards = await GiftCardManager.findGiftCardsByCode(code, true);
  } catch (error) {
    window.alertUser(
      Localize.translate(
        'An unexpected error has occurred while searching gift cards. Please try again.'
      )
    );

    View.resetCodeInput();
    enableFormSubmission();
    hideGiftCardLoader();

    return;
  }

  const queryResult = giftCards.find((giftCard) => giftCard.code === code);

  if (queryResult && queryResult?.status === GIFT_CARD_STATUS.AWAITING_ACTIVATION) {
    document.getElementById('new_gift_card_code')?.blur();
    const message =
      `${Localize.translate('This gift card code already exists.')} ` +
      `<a class="danger" target=”_blank” href="/register.php?form_name=load_transaction&transaction_id=${
        queryResult.orderId
      }&tab=details">${Localize.translate(`View transaction.`)}</a>`;

    addErrorMessage('new_gift_card_code', message);

    window.showNotifier('warning', message);

    hideGiftCardLoader();
    View.resetGiftCardResults();

    return;
  }

  if (!queryResult) {
    const card: GiftCard = { code, createdAt: null, customerId: null, uuid: null };
    giftCards.push(card);
  }

  const customerIds = giftCards.map((giftCard) => giftCard.customerId).filter((id) => id);

  let customers: Customer[] = [];

  try {
    customers = await GiftCardManager.getCustomers(customerIds);
  } catch (error) {
    // Safe catch.
  }

  const showRadioButton = giftCards.length > 1;

  const inputList = giftCards.map((giftCard) => {
    const customer = customers.find((customer) => customer.customerID === giftCard.customerId);

    return View.renderGiftCardInput(giftCard, customer, code, showRadioButton);
  });

  View.updateGiftCardResults(inputList.join(''));
  enableFormSubmission();
  hideGiftCardLoader();
}, SEARCH_DEBOUNCE_RATE_MS);

function hideGiftCardLoader(): void {
  const loaderElement = document.getElementById('gc-searching');

  loaderElement?.classList.add('hidden');
}

function showGiftCardLoader(): void {
  const loaderElement = document.getElementById('gc-searching');

  loaderElement?.classList.remove('hidden');
}

function enableFormSubmission() {
  const submitButton = document.getElementById('createGiftCardButton') as HTMLButtonElement | null;

  if (submitButton) {
    submitButton.disabled = false;
  }
}

function disableFormSubmission() {
  const submitButton = document.getElementById('createGiftCardButton') as HTMLButtonElement | null;

  if (submitButton) {
    submitButton.disabled = true;
  }
}

export function checkForMultipleGiftCards(code: string): void {
  removeErrorMessage('new_gift_card_code');

  // Avoids input from calling endpoint multiple times from onchange and oninput
  // onchange is for scanner functionality
  const currentGiftCardCode = document.getElementById('previous_code') as HTMLInputElement | null;

  if (currentGiftCardCode != null && currentGiftCardCode.value === code) return;

  if (currentGiftCardCode) {
    currentGiftCardCode.value = code;
  }

  // Don't wait for the debouncing so refreshing the UI is slightly faster.
  if (code.length >= 8) {
    // The submission only needs to be disabled after 8 characters have been
    // entered as the `min-length` attribute will prevent the form to be
    // submitted.
    disableFormSubmission();
    showGiftCardLoader();
  } else {
    View.resetGiftCardResults();
    enableFormSubmission();
  }

  refreshGiftCardInputs(code);
}

function generateRandomString(length: number) {
  let result = '';
  const characters = '0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function randomGenerateGiftCardCode(event: InputEvent): void {
  event.preventDefault();

  const inputEvent = new InputEvent('input');
  const giftCardInput = document.getElementById('new_gift_card_code') as HTMLInputElement;

  const randomGiftCardCode = generateRandomString(16);

  if (giftCardInput) {
    giftCardInput.value = randomGiftCardCode;
  }

  giftCardInput.dispatchEvent(inputEvent);
}

export function toggleElectronicDetails(): void {
  const detailsSection = document.getElementById('electronic-details-section');
  const toggle = document.getElementById('electronic-delivery-toggle') as HTMLInputElement | null;

  if (toggle?.checked) {
    detailsSection?.classList.remove('ogc-hide');
    detailsSection?.classList.add('ogc-show');
  } else {
    detailsSection?.classList.remove('ogc-show');
    detailsSection?.classList.add('ogc-hide');
  }
}

export function updateCharCount(): void {
  const textarea = document.getElementById(
    'gift_card_personal_message'
  ) as HTMLTextAreaElement | null;
  const charCountElem = document.getElementById(
    'gift_card_personal_message_count'
  ) as HTMLInputElement | null;

  if (!charCountElem || !textarea) return;
  const currentLength = textarea.value.length;
  const translatedCharCount = Localize.translate('characters');
  charCountElem.textContent = `${currentLength}/${MAX_PERSONAL_MESSAGE_LENGTH} ${translatedCharCount}`;
  if (currentLength >= MAX_PERSONAL_MESSAGE_LENGTH) {
    charCountElem.classList.remove('char-count-default');
    charCountElem.classList.add('char-count-exceeded');
  } else {
    charCountElem.classList.remove('char-count-exceeded');
    charCountElem.classList.add('char-count-default');
  }
}

export function removeErrorMessage(id: string): void {
  const inputElem = document.getElementById(id) as HTMLInputElement | null;
  const errorMessageElem = document.getElementById(`${id}-error`) as HTMLInputElement | null;
  if (errorMessageElem && inputElem && inputElem.value.trim()) {
    errorMessageElem.remove();
    inputElem.style.borderColor = '';
    inputElem.classList.remove('error');
    inputElem.removeAttribute('aria-invalid');
    inputElem.removeAttribute('aria-describedby');
  }
}

function addErrorMessage(id: string, message: string): void {
  const inputElem = document.getElementById(id) as HTMLInputElement | null;
  if (!inputElem) return;

  inputElem.classList.add('error');
  inputElem.setAttribute('aria-invalid', 'true');
  inputElem.setAttribute('aria-describedby', `${id}-error`);

  const errorDiv = document.createElement('div');
  errorDiv.id = `${id}-error`;
  errorDiv.innerHTML = message;
  errorDiv.classList.add('ogc-error');
  errorDiv.setAttribute('role', 'alert');
  errorDiv.setAttribute('aria-live', 'polite');
  inputElem?.parentNode?.insertBefore(errorDiv, inputElem?.nextSibling);
}
